import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthState, AUTH_CONSTANTS } from 'app/core/auth/auth.types';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap, catchError, take, timeout, delay } from 'rxjs/operators';

/**
 * Closure state for caching (shared across all guard invocations)
 */
const authCache = new Map<string, { result: boolean | UrlTree; timestamp: number }>();
let lastGlobalCheck: { result: boolean; timestamp: number } | null = null;

function isGlobalCacheValid(): boolean {
    if (!lastGlobalCheck) return false;
    return (Date.now() - lastGlobalCheck.timestamp) < AUTH_CONSTANTS.CACHE_DURATION;
}

function getCachedResult(key: string): boolean | UrlTree | null {
    const cached = authCache.get(key);
    if (!cached) return null;
    if ((Date.now() - cached.timestamp) > AUTH_CONSTANTS.CACHE_DURATION) {
        authCache.delete(key);
        return null;
    }
    return cached.result;
}

function setCachedResult(key: string, result: boolean | UrlTree): void {
    authCache.set(key, { result, timestamp: Date.now() });
    // Clean up old entries
    if (authCache.size > 50) {
        const oldest = authCache.keys().next().value;
        if (oldest) authCache.delete(oldest);
    }
}

/**
 * Enhanced AuthGuard (Angular 19 functional CanActivateFn)
 *
 * Features:
 * - State machine-aware auth checking
 * - Retry logic for transient failures
 * - Timeout handling
 * - Authentication result caching
 * - Proper redirect URL preservation
 */
export const AuthGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const redirectURL = state.url;

    // Listen for auth state changes to clear cache
    authService.authStatus$.pipe(take(1)).subscribe(authStatus => {
        if (authStatus.state === AuthState.AUTHENTICATED) {
            authCache.clear();
            lastGlobalCheck = null;
        }
    });

    // Check global authentication state first for better performance
    if (lastGlobalCheck && isGlobalCacheValid() && lastGlobalCheck.result) {
        return of(true);
    }

    // Check route-specific cache
    const cached = getCachedResult(redirectURL);
    if (cached !== null) {
        return of(cached);
    }

    return authService.checkAuthentication().pipe(
        timeout(AUTH_CONSTANTS.AUTH_CHECK_TIMEOUT),
        map(authStatus => {
            let result: boolean | UrlTree;

            switch (authStatus.state) {
                case AuthState.AUTHENTICATED:
                    result = true;
                    break;

                case AuthState.UNAUTHENTICATED:
                    result = router.createUrlTree(['/sign-in'], {
                        queryParams: { redirectURL }
                    });
                    break;

                case AuthState.EXPIRED:
                    result = router.createUrlTree(['/sign-in'], {
                        queryParams: {
                            redirectURL,
                            message: 'Your session has expired. Please sign in again.'
                        }
                    });
                    break;

                case AuthState.ERROR:
                    result = router.createUrlTree(['/sign-in'], {
                        queryParams: {
                            redirectURL,
                            message: 'Authentication error. Please sign in again.'
                        }
                    });
                    break;

                case AuthState.INITIALIZING:
                default:
                    result = router.createUrlTree(['/sign-in'], {
                        queryParams: { redirectURL }
                    });
                    break;
            }

            // Cache the result
            setCachedResult(redirectURL, result);
            if (typeof result === 'boolean') {
                lastGlobalCheck = { result, timestamp: Date.now() };
            }

            return result;
        }),
        catchError(error => {
            console.error('AuthGuard: Auth check error, attempting fallback:', error);

            // Fallback: use legacy check
            return authService.check().pipe(
                timeout(2000),
                map(authenticated => {
                    if (authenticated) return true;
                    return router.createUrlTree(['/sign-in'], {
                        queryParams: { redirectURL }
                    });
                }),
                catchError(() => {
                    return of(router.createUrlTree(['/sign-in'], {
                        queryParams: {
                            redirectURL,
                            message: 'Unable to verify authentication. Please sign in.'
                        }
                    }));
                })
            );
        })
    );
};
